import { useEffect, useState } from "react";
import { Drawer, DrawerContent, DrawerNavigation } from "@progress/kendo-react-layout";
import { FaTimesCircle } from "react-icons/fa";
import { getTimesheetByEmployeeIdYearWeek, TimesheetEntry } from "../../data/timesheetData";
import { Form, Field, FormElement } from '@progress/kendo-react-form'
import { FormDropDownListFilter } from "../../components/FormComps/FormComponents";
import { getEmployeeDropdown } from "../../data/employeeData";
import { dropDown } from "../../data/interfaces";
import TimesheetsGrid from "../../components/grids/TimesheetsGrid";
import './Timesheets.css';
import TimesheetForm from "../../components/FormComps/Timesheets/TimesheetForm";
import { canEditArea } from "../../helpers/areaAccess";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isoWeek);


interface TimesheetsProps {
    allowedRoutes?: string[];
}

export default function Timesheets(props: TimesheetsProps) {
    // Timesheets.Manage or an administrator (see areaAccess.ts).
    const canEdit = canEditArea(props.allowedRoutes, "Timesheets");

    const [expanded, setExpanded] = useState(false);
    const [selectedId, setSelectedId] = useState<number>(0);
    const [formVisible, setFormVisible] = useState(false);
    const [mode, setMode] = useState<"edit" | "add">("edit");
    const [selectedEmployee, setSelectedEmployee] = useState<dropDown | undefined>(undefined);
    const [employeesForDropdown, setEmployeesForDropdown] = useState<dropDown[]>([]);
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState<dropDown>({ text: currentYear.toString(), value: currentYear });

    const [timesheetData, setTimesheetData] = useState<TimesheetEntry[]>([]);
    const [message, setMessage] = useState<string>('Please select an employee, year and week to view timesheet data.');

    /**
     * Generates an array of dropdown options representing weeks of the year.
     * Each option contains a text label in the format "Week X" and a corresponding value.
     * 
     * @constant
     * @type {dropDown[]}
     */

    
    const weeks: dropDown[] = Array.from({ length: 52 }, (_, i) => {
        // Get the first Monday of the first ISO week
        const startOfWeek = dayjs().startOf("year").isoWeek(1).startOf("isoWeek").add(i, "week");
        const endOfWeek = startOfWeek.add(6, "day");
    
        // Formatting function
        const formatDate = (date: dayjs.Dayjs) => {
            const day = date.date();
            const suffix = ["th", "st", "nd", "rd"][(day % 10 > 3 || [11, 12, 13].includes(day % 100)) ? 0 : day % 10];
            return `${day}${suffix} ${date.format("MMM")}`;
        };
    
        return {
            text: `Week ${i + 1} (${formatDate(startOfWeek)} - ${formatDate(endOfWeek)})`,
            value: i + 1,
        };
    });
    
    const [searchedWeeks, setSearchedWeeks] = useState<dropDown[]>(weeks);
    const currentWeek = weeks.find(week => week.value === Math.ceil((new Date().getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000)))?.value || 1;
    const currentWeekText = weeks.find(week => week.value === currentWeek)?.text || `Week ${currentWeek}`;
    const [selectedWeek, setSelectedWeek] = useState<dropDown>({ text: currentWeekText, value: currentWeek });
    /**
     * Generates an array of dropdown options representing the 7 days of a given week.
     * Each option contains a text label in the format "Monday" and a corresponding date value.
     * 
     * @param {number} weekNumber - The week number for which to generate the days.
     * @param {number} year - The year for which to generate the days.
     * @returns {dropDown[]} An array of objects, each containing the text and value of a day.
     */
    const getWeekDays = (weekNumber: number, year: number): dropDown[] => {
        const firstDayOfWeek = dayjs().year(year).isoWeek(weekNumber).startOf("isoWeek");
        const weekDays: dropDown[] = [];

        for (let i = 0; i < 7; i++) {
            const currentDay = firstDayOfWeek.add(i, "day");
            weekDays.push({
                text: `${currentDay.format('dddd')} (${currentDay.format('D')}${['th', 'st', 'nd', 'rd'][(currentDay.date() % 10 === 1 && currentDay.date() % 100 !== 11) ? 1 : (currentDay.date() % 10 === 2 && currentDay.date() % 100 !== 12) ? 2 : (currentDay.date() % 10 === 3 && currentDay.date() % 100 !== 13) ? 3 : 0]})`,
                value: i,
                data: currentDay.format('YYYY-MM-DD'),
            });
        }
        console.log(weekDays)
        return weekDays;
    };

    /**
     * Creates an array of dropdown options representing years from 2024 to the current year.
     *
     * @returns {dropDown[]} An array of objects, each containing the text and value of a year.
     */
    const createYearsArray = (): dropDown[] => {
        const currentYear = new Date().getFullYear();
        const years: dropDown[] = [];
        for (let year = 2024; year <= currentYear; year++) {
            years.push({ text: year.toString(), value: year });
        }
        return years;
    }

    const [searchedYears, setSearchedYears] = useState<dropDown[]>(createYearsArray());

    /**
     * Searches for employees based on the provided search term and updates the dropdown list.
     *
     * @param {string} searchTerm - The term to search for employees.
     * @returns {Promise<void>} - A promise that resolves when the search is complete.
     * @throws Will log an error to the console if the search fails.
     */
    const searchEmployeeDropdown = async (searchTerm: string) => {
        try {
            const res = await getEmployeeDropdown(searchTerm)
            setEmployeesForDropdown(res)
        } catch (err) {
            console.log(err)
        }
    }

    /**
     * Fetches the employee dropdown data and stores it in the state.
     * 
     * This function makes an asynchronous call to retrieve the employee dropdown
     * data. If the call is successful, it updates the state with the retrieved data.
     * If an error occurs during the fetch, it logs the error to the console.
     * 
     * @async
     * @function getAndStoreEmployees
     * @returns {Promise<void>} A promise that resolves when the employee data has been fetched and stored.
     */
    const getAndStoreEmployees = async () => {
        try {
            const res = await getEmployeeDropdown('');
            setEmployeesForDropdown(res);
        } catch (err) {
            console.log(err)
        }
    }

    const getAndStoreTimesheetData = async () => {
        if (selectedEmployee && selectedYear && selectedWeek) {
            try {
                const res = await getTimesheetByEmployeeIdYearWeek(selectedEmployee.value, selectedYear.value, selectedWeek.value)
                setTimesheetData(res);
                if (res.length === 0) {
                    setMessage('No timesheet data found for the selected employee, year and week.')
                }
            } catch (err) {
                console.log(err)
            }
        }
    }

    /**
     * Toggles the expanded state of the component.
     */
    const handleExpanded = () => {
        setExpanded((prevState) => {
            if (prevState) {
                // Clear form state when closing the sidebar
                setSelectedId(0);
                setFormVisible(false);
                setMode("edit");
            }
            return !prevState;
        });
    };

    /**
     * Handles the edit form for a specific ID.
     * 
     * @param id - The ID of the item to be edited.
     */
    const handleEditForm = (id: number) => {
        setSelectedId(id);
        setFormVisible((prevState) => (!prevState ? true : true));
        setMode("edit");
        handleExpanded();
    };

    /**
     * Handles the selection event.
     * Sets the expanded state to false.
     */
    const handleSelect = () => {
        setExpanded(false);
    };

    /**
     * Handles the action of adding a form.
     */
    const handleAddForm = () => {
        setFormVisible((prevState) => (!prevState ? true : true));
        setMode('add')
        handleExpanded();
    };

    useEffect(() => {
        getAndStoreEmployees()
    }, []);

    /**
     * Searches for a week in the weeks array based on the provided search term.
     *
     * @param {string} searchTerm - The term to search for in the weeks array.
     * @returns {dropDown | undefined} - The matching week object or undefined if not found.
     */
    const searchWeekDropdown = (searchTerm: string): void => {
        const results = weeks.filter(week => week.text.toLowerCase().includes(searchTerm.toLowerCase()));
        if (results) {
            setSearchedWeeks(results)
        }
    };

    /**
     * Searches for a year in the years array based on the provided search term.
     *
     * @param {string} searchTerm - The term to search for in the years array.
     * @returns {dropDown | undefined} - The matching year object or undefined if not found.
     */
    const searchYearDropdown = (searchTerm: string): void => {
        const years = createYearsArray();
        const results = years.filter(year => year.text.toLowerCase().includes(searchTerm.toLowerCase()));
        if (results) {
            setSearchedYears(results);
        }
    };

    useEffect(() => {
        getAndStoreTimesheetData();
    }, [selectedEmployee, selectedYear, selectedWeek]);


    return (
        <div>
            <Form
                render={(formRenderProps) => (
                    <FormElement >
                        <fieldset className="timesheet-fieldset" style={{ background: 'rgb(30, 42, 78)', padding: "0 1.5rem 1.5rem", borderRadius: "0.5rem", display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', justifyContent: 'center', margin: "0 25px" }}>
                            <Field
                                data={employeesForDropdown}
                                name={'employee'}
                                component={FormDropDownListFilter}
                                label={'Select employee: *'}
                                setData={setEmployeesForDropdown}
                                placeholder={'Select an employee:'}
                                searchDropdown={searchEmployeeDropdown}
                                onChange={(event: any) => {
                                    setSelectedEmployee(event.target.value)
                                }}
                            />
                            <Field
                                data={searchedWeeks}
                                name={'weeks'}
                                defaultItem={selectedWeek}
                                component={FormDropDownListFilter}
                                label={'Select a week: *'}
                                setData={setSearchedWeeks}
                                placeholder={'Select a week:'}
                                searchDropdown={searchWeekDropdown}
                                onChange={(event: any) => {
                                    setSelectedWeek(event.target.value)
                                }}
                            />
                            <Field
                                data={searchedYears}
                                name={'years'}
                                defaultItem={selectedYear}
                                component={FormDropDownListFilter}
                                label={'Select a year: *'}
                                setData={setSearchedYears}
                                placeholder={'Select a year:'}
                                searchDropdown={searchYearDropdown}
                                onChange={(event: any) => {
                                    setSelectedYear(event.target.value)
                                }}
                            />
                        </fieldset>
                    </FormElement>
                )}
            />

            <Drawer
                expanded={expanded}
                position={"end"}
                width={750}
                mode="overlay"
                animation={{
                    duration: 400,
                }}
                onOverlayClick={handleExpanded}
                onSelect={handleSelect}
            >
                <DrawerContent>
                    <DrawerNavigation>
                        {formVisible && (
                            <>
                                <div className="form__title__container">
                                    <h1 className="form__title">{mode === "edit" ? (canEdit ? "Edit Timesheet Entry" : "Timesheet Entry") : "Add New Timesheet Entry"}</h1>
                                    <FaTimesCircle
                                        className="closeform"
                                        onClick={handleExpanded}
                                    />
                                </div>
                                <TimesheetForm
                                    weekDays={getWeekDays(selectedWeek.value, selectedYear.value)}
                                    id={selectedId}
                                    mode={mode}
                                    employeeId={selectedEmployee?.value}
                                    getTableData={getAndStoreTimesheetData}
                                    handleExpanded={handleExpanded}
                                    readOnly={!canEdit}
                                />

                            </>
                        )}
                    </DrawerNavigation>
                    <TimesheetsGrid
                        onAddToggle={handleAddForm}
                        onEditToggle={handleEditForm}
                        onData={timesheetData}
                        canEdit={canEdit}
                    />
                    {
                        ! timesheetData.length ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <p style={{ fontSize: "1.2rem", color: "black", fontWeight: "bold", marginTop: "30px" }}>{message}</p>
                            </div>
                        ) : null
                    }
                </DrawerContent>
            </Drawer>
        </div>
    )
}